import { Box, Button, Divider } from '@wix/design-system';
import { SwitchProductModule } from '@wix/package-picker-sdk';
import { GtmEvents, useGtmLogger } from '@wix/package-picker-shared';
import {
  PageDisclaimer,
  TrustBuilders,
  useInitSelectPlanPageBiEvents,
  useStudioPanorama,
} from '@wix/package-picker-shared-studio';
import { QueriesLoaderObserver } from '@wix/premium-fed-sdk/@apis-infra-react';
import { ScrollToTopButton } from '@wix/premium-purchase-platform/@ui';
import React from 'react';
import {
  AllPlansIncludedFeaturesBanner,
  FeaturesGrid,
  PageLayout,
  ProductCards,
} from '../../components';
import { AfnicTrustBuilder } from '../../components/AfnicTrustBuilder/AfnicTrustBuilder';
import { StudioPackagePickerModule } from '../../modules/studio-package-picker.module';
import { SelectPlanPageSkeleton } from './SelectPlanPageSkeleton';

export const SelectPlanPage = () => {
  const { t } = StudioPackagePickerModule.internals.useTranslations();
  const panorama = useStudioPanorama();
  const gridRef = React.useRef<HTMLDivElement>(null);
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const { didInit } = useInitSelectPlanPageBiEvents();
  const didReportLoad = React.useRef(false);
  useGtmLogger(GtmEvents.SELECT_PLAN);

  const {
    isDowngradeFlow,
    isUpgradeFlow,
    isLoading: isSwitchFlowsLoading,
  } = SwitchProductModule.SDK.hooks.useSwitchFlows();

  React.useEffect(() => {
    if (!isSwitchFlowsLoading) {
      if (isUpgradeFlow) {
        document.title = t('seo.studio.second_upgrade.title');
      } else if (isDowngradeFlow) {
        document.title = t('seo.studio.change_plan.app_title');
      }
    }
  }, [isDowngradeFlow, isUpgradeFlow, isSwitchFlowsLoading]);

  React.useEffect(() => {
    panorama.startTransaction('select-plan-page-load');
  }, []);

  return (
    <QueriesLoaderObserver
      Loading={<SelectPlanPageSkeleton />}
      onFinishLoading={() => {
        if (didReportLoad.current || !didInit) {
          return;
        }
        StudioPackagePickerModule.internals.bi.onSelectPlanPageLoad?.({});
        panorama.finishTransaction('select-plan-page-load');
        didReportLoad.current = true;
      }}
    >
      <PageLayout>
        <Box width="100%" align="center" marginTop="72px">
          <ProductCards />
        </Box>
        <Box marginTop="SP5">
          <Button
            skin="dark"
            priority="secondary"
            onClick={() => {
              gridRef.current?.scrollIntoView({ behavior: 'smooth' });
              StudioPackagePickerModule.internals.bi.onComparePlanFeaturesClick?.(
                {},
              );
            }}
          >
            {t('premium-purchase-plan.cards.compare_features.cta')}
          </Button>
        </Box>
        <div ref={scrollRef} />
        <Box width="100%" margin="SP5 0">
          <AllPlansIncludedFeaturesBanner />
        </Box>
        <Box
          width="100%"
          marginTop="SP3"
          maxWidth="1140px"
          direction="vertical"
          gap="SP2"
        >
          {/* TODO: Move to reusable desktop component */}
          <Box direction="vertical" gap="30px">
            <PageDisclaimer />
            <Divider />
            <TrustBuilders />
          </Box>
          <AfnicTrustBuilder />
        </Box>
        <Box width="100%" align="center" maxWidth="1140px" marginTop="SP7">
          <FeaturesGrid innerRef={gridRef} />
        </Box>
        <ScrollToTopButton
          componentRef={scrollRef}
          text={t('studio_package_picker.table.go_to_top')}
          onClick={() => {
            StudioPackagePickerModule.internals.bi.onScrollToTopClick?.({});
          }}
        />
      </PageLayout>
    </QueriesLoaderObserver>
  );
};
